import React, {FormEvent, useCallback, useContext, useEffect, useMemo, useState} from "react";
import log from "loglevel";
import {
  Pref,
  SearchIndicatorConfigsDocument,
  SetIndicatorConfigMutationVariables,
  UserPref,
  useSearchIndicatorConfigsLazyQuery,
  useSetIndicatorConfigMutation,
  useSetTenantPrefMutation
} from "../../../../generated/graphql";
import {TenantPreferences} from "../../../../constants/constants";
import {extractSimplePref, PreferenceContext, PrefValue} from "../../../../providers/PreferenceProvider";
import "../../CompanySettings/common/CompanySettings.css";
import _ from "lodash";
import {
  ContentNumericProp,
  ContentOptionProps,
  ContentSingleSelectProp,
  DEFAULT_DWELL_MINUTES,
  DEFAULT_NUMBER_OF_UPCOMING_STOPS,
  MAX_DWELL_MINUTES,
  MAX_NUMBER_OF_UPCOMING_STOPS,
  MIN_NUMBER_OF_UPCOMING_STOPS,
  DEFAULT_GPS_FREQUENCY_MINUTES,
  GPS_FREQUENCY_MINUTES_OPTIONS
} from "../../CompanySettings/common/constants";
import {ContentProps} from "../../CompanySettings/common/components/Content";
import {
  Body,
  CancelButton,
  Container,
  Footer,
  Header,
  HeaderText,
  SaveButton
} from "../../CompanySettings/common/styles";
import Section from "../../CompanySettings/common/components/Section";
import {renderRadioGroupProps} from "../../CompanySettings/common/renderer";
import {AuthContext} from "../../../../providers/AuthProvider";
import {useFeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {ComparisonTypes} from "../../ColorSettings/ColorizedIndicators/types/indicatorLogicType";
import {getRankLogicsGroupedByColor} from "../../ColorSettings/ColorizedIndicators/ColorRank.service";
import {IndicatorLogicSettings} from "../../ColorSettings/ColorizedIndicators/types/indicatorLogicSettings";
import {ConfirmationDialog} from "../../../common/ConfirmationDialog";

const ETA = () => {
  const {isSuperUser, isEcourierUser} = useContext(AuthContext);
  const {etaWaitForEarlyTime} = useFeatureFlagContext();
  const {tenantPreferences, tenantPrefsQueryRefetch} = useContext(PreferenceContext);
  const [setTenantPref, {loading: isSetTenantPrefLoading, data: setTenantPrefData}] = useSetTenantPrefMutation();
  const [setIndicatorConfigMutation] = useSetIndicatorConfigMutation();
  const [fetchColorizedIndicatorData] = useSearchIndicatorConfigsLazyQuery();

  const [useETAService, setUseETAService] = useState<boolean>(true);
  const [initUseETAService, setInitUseETAService] = useState<boolean>(true);
  const [isOpenETAServiceConfirmationDialog, setIsOpenETAServiceConfirmationDialog] = useState<boolean>(false);

  const [useGPSPoints, setUseGPSPoints] = useState<boolean>(true);
  const [initUseGPSPoints, setInitUseGPSPoints] = useState<boolean>(true);

  const [gpsFrequency, setGPSFrequency] = useState<number | undefined>(undefined);
  const [initGPSFrequency, setInitGPSFrequency] = useState<number | undefined>(undefined);

  const [numberOfUpcomingStops, setNumberOfUpcomingStops] = useState<number | undefined>(undefined);
  const [initNumberOfUpcomingStops, setInitNumberOfUpcomingStops] = useState<number | undefined>(undefined);

  const [assumedDwellTime, setAssumedDwellTime] = useState<number | undefined>(undefined);
  const [initAssumedDwellTime, setInitAssumedDwellTime] = useState<number | undefined>(undefined);

  const [waitForEarlyTimeWindow, setWaitForEarlyTimeWindow] = useState<boolean>(false);
  const [initWaitForEarlyTimeWindow, setInitWaitForEarlyTimeWindow] = useState<boolean>(false);

  const isInternalSuperAdmin = useMemo(() => {
    return Boolean(isEcourierUser && isSuperUser);
  }, [isEcourierUser, isSuperUser]);

  const maxNumberOfUpcomingStopsByRole = useMemo(() => {
    if (isInternalSuperAdmin) {
      return Infinity;
    }
    return MAX_NUMBER_OF_UPCOMING_STOPS;
  }, [isInternalSuperAdmin]);

  const updateTenantPrefs = useCallback(
    (key: string, value: any) => {
      setTenantPref({
        variables: {
          name: key,
          input: {
            value: JSON.stringify({value: value})
          }
        }
      })
        .then(() => {
          if (tenantPrefsQueryRefetch) tenantPrefsQueryRefetch();
        })
        .catch((error: Error) => {
          log.error(error);
        });
    },
    [setTenantPref, tenantPrefsQueryRefetch]
  );

  const handleUseETAServiceChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    if (!selectedValue) {
      setIsOpenETAServiceConfirmationDialog(true);
      return;
    }
    setUseETAService(selectedValue);
  }, []);

  const handleCloseETAServiceConfirmationDialog = useCallback(() => {
    setIsOpenETAServiceConfirmationDialog(false);
  }, []);

  const handleYesETAServiceConfirm = useCallback(() => {
    setUseETAService(false);
    handleCloseETAServiceConfirmationDialog();
  }, [handleCloseETAServiceConfirmationDialog]);

  const handleUseGPSPointsChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    if (selectedValue) {
      setGPSFrequency(DEFAULT_GPS_FREQUENCY_MINUTES);
    }
    setUseGPSPoints(selectedValue);
  }, []);

  const handleGPSFrequencyChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue: number | undefined = parseInt(event.currentTarget.value);
    setGPSFrequency(selectedValue);
  }, []);

  const handleNumberOfUpcomingStopsChange = useCallback(
    (event: FormEvent<HTMLInputElement>): void => {
      let selectedValue: number | undefined = parseInt(event.currentTarget.value);
      if (isNaN(selectedValue) || selectedValue < 0) {
        selectedValue = 0;
      }
      if (selectedValue > maxNumberOfUpcomingStopsByRole) {
        return;
      }
      setNumberOfUpcomingStops(selectedValue);
    },
    [maxNumberOfUpcomingStopsByRole]
  );

  const handleNumberOfUpcomingStopsBlur = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      if (Number((event.target as HTMLInputElement).value) < MIN_NUMBER_OF_UPCOMING_STOPS) {
        handleNumberOfUpcomingStopsChange({
          currentTarget: {value: MIN_NUMBER_OF_UPCOMING_STOPS.toString()} as HTMLInputElement
        } as FormEvent<HTMLInputElement>);
      }
    },
    [handleNumberOfUpcomingStopsChange]
  );

  const handleWaitForEarlyTimeWindowChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    setWaitForEarlyTimeWindow(Boolean(JSON.parse(event.currentTarget.value)));
  }, []);

  const handleAssumedDwellTimeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    let selectedValue: number | undefined = parseInt(event.currentTarget.value);
    if (isNaN(selectedValue) || selectedValue < 0) {
      selectedValue = 0;
    }
    if (selectedValue > MAX_DWELL_MINUTES) {
      return;
    }
    setAssumedDwellTime(selectedValue);
  }, []);

  const isUseETAServiceDirty = useCallback(() => {
    return initUseETAService !== useETAService;
  }, [initUseETAService, useETAService]);

  const isUseGPSPointsDirty = useCallback(() => {
    return initUseGPSPoints !== useGPSPoints;
  }, [initUseGPSPoints, useGPSPoints]);

  const isGPSFrequencyDirty = useCallback(() => {
    return initGPSFrequency !== gpsFrequency;
  }, [gpsFrequency, initGPSFrequency]);

  const isNumberOfUpcomingStopsDirty = useCallback(() => {
    return initNumberOfUpcomingStops !== numberOfUpcomingStops;
  }, [initNumberOfUpcomingStops, numberOfUpcomingStops]);

  const isAssumedDwellTimeDirty = useCallback(() => {
    return initAssumedDwellTime !== assumedDwellTime;
  }, [assumedDwellTime, initAssumedDwellTime]);

  const isWaitForEarlyTimeWindowDirty = useCallback(() => {
    return initWaitForEarlyTimeWindow !== waitForEarlyTimeWindow;
  }, [initWaitForEarlyTimeWindow, waitForEarlyTimeWindow]);

  const isSaveChange = () => {
    return (
      isUseGPSPointsDirty() ||
      isGPSFrequencyDirty() ||
      isAssumedDwellTimeDirty() ||
      isNumberOfUpcomingStopsDirty() ||
      isUseETAServiceDirty() ||
      isWaitForEarlyTimeWindowDirty()
    );
  };

  const handleSaveSetting = useCallback(() => {
    if (isUseETAServiceDirty()) {
      updateTenantPrefs(TenantPreferences.useEtaService, useETAService);
      if (!useETAService) {
        fetchColorizedIndicatorData({
          variables: {
            filter: {
              siteId: {
                eq: 0
              }
            }
          },
          onCompleted: (data) => {
            if (data?.searchIndicatorConfigs?.items && data.searchIndicatorConfigs.items.length > 0) {
              const logicData: IndicatorLogicSettings = JSON.parse(
                data?.searchIndicatorConfigs?.items[0]?.config
              ) as IndicatorLogicSettings;
              const sk = data?.searchIndicatorConfigs?.items[0]?.sk;
              const remainingRules = logicData?.rules?.filter(
                (rule: any) =>
                  rule.comparison !== ComparisonTypes.ETAToLateTime &&
                  rule.comparison !== ComparisonTypes.ETAToScheduledTime
              );
              const updatedData = {
                ...logicData,
                rules: getRankLogicsGroupedByColor(remainingRules)
              } as IndicatorLogicSettings;

              const variables: SetIndicatorConfigMutationVariables = {
                input: {
                  config: JSON.stringify(updatedData),
                  siteId: 0
                }
              };

              if (sk) {
                variables["sk"] = sk;
              }

              setIndicatorConfigMutation({
                refetchQueries: [SearchIndicatorConfigsDocument],
                variables: variables
              });
            }
          },
          onError: (error) => {
            log.error("Error fetching colorized indicator data: ", error);
          }
        });
      }
    }

    if (isUseGPSPointsDirty()) {
      updateTenantPrefs(TenantPreferences.useGPSPoints, useGPSPoints);
    }
    if (isGPSFrequencyDirty()) {
      updateTenantPrefs(TenantPreferences.gpsFrequency, gpsFrequency);
    }
    if (isNumberOfUpcomingStopsDirty()) {
      updateTenantPrefs(TenantPreferences.numberOfUpcomingStops, numberOfUpcomingStops);
    }
    if (isAssumedDwellTimeDirty()) {
      updateTenantPrefs(TenantPreferences.assumedETADwellTime, assumedDwellTime);
    }
    if (isWaitForEarlyTimeWindowDirty()) {
      updateTenantPrefs(TenantPreferences.waitForEarlyTimeWindow, waitForEarlyTimeWindow);
    }
  }, [
    isUseETAServiceDirty,
    isUseGPSPointsDirty,
    isGPSFrequencyDirty,
    isNumberOfUpcomingStopsDirty,
    isAssumedDwellTimeDirty,
    isWaitForEarlyTimeWindowDirty,
    updateTenantPrefs,
    useETAService,
    fetchColorizedIndicatorData,
    setIndicatorConfigMutation,
    useGPSPoints,
    gpsFrequency,
    numberOfUpcomingStops,
    assumedDwellTime,
    waitForEarlyTimeWindow
  ]);

  const handleCancelSetting = useCallback(() => {
    setUseETAService(initUseETAService);
    setUseGPSPoints(initUseGPSPoints);
    setGPSFrequency(initGPSFrequency);
    setNumberOfUpcomingStops(initNumberOfUpcomingStops);
    setAssumedDwellTime(initAssumedDwellTime);
    setWaitForEarlyTimeWindow(initWaitForEarlyTimeWindow);
  }, [
    initUseETAService,
    initUseGPSPoints,
    initGPSFrequency,
    initNumberOfUpcomingStops,
    initAssumedDwellTime,
    initWaitForEarlyTimeWindow
  ]);

  const renderETASettings = useMemo(() => {
    const retVal: ContentProps[] = [];

    retVal.push(
      {
        ...ContentOptionProps,
        contentTitle: "Calculate ETAs",
        contentInformation: "Determines if the system calculate ETAs for stops.",
        contentOptions: [
          {
            group: "calculateETAs",
            label: "Yes",
            value: "true"
          },
          {
            group: "calculateETAs",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleUseETAServiceChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(useETAService)
      },
      {
        ...ContentOptionProps,
        contentTitle: "Use GPS Points",
        contentInformation: "Determines if the system should use GPS points to calculate the ETA of stops.",
        contentOptions: [
          {
            group: "useGPSPoints",
            label: "Yes",
            value: "true"
          },
          {
            group: "useGPSPoints",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleUseGPSPointsChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(useGPSPoints),
        isDisabled: () => !useETAService
      },
      {
        ...ContentSingleSelectProp,
        contentTitle: "GPS Frequency",
        contentInformation: <div>The frequency of ETA calcualtion using GPS points.</div>,
        contentUnit: "minutes",
        contentOptions: GPS_FREQUENCY_MINUTES_OPTIONS,
        selectedOption: _.toNumber(gpsFrequency),
        handleChangeOption: handleGPSFrequencyChange,
        isDisabled: () => !isInternalSuperAdmin || !useETAService,
        tooltipContent: () => {
          if (isInternalSuperAdmin) {
            return "";
          }
          return (
            <div>
              Please send an email to{" "}
              <a
                href="mailto:support@e-courier.com"
                style={{textDecoration: "underline", color: "#137cbd"}}
                target="_blank"
                rel="noopener noreferrer"
              >
                support@e-courier.com
              </a>{" "}
              about changing the GPS Frequency.
            </div>
          );
        }
      }
    );

    retVal.push(
      {
        ...ContentNumericProp,
        contentTitle: "Number of Upcoming Stops",
        contentInformation: (
          <div>
            The number of upcoming incomplete stops that will receive an ETA when there is a new ETA event.
            <br />
            {isInternalSuperAdmin ? (
              `Minimum: ${MIN_NUMBER_OF_UPCOMING_STOPS}.`
            ) : (
              <>
                Minimum: {MIN_NUMBER_OF_UPCOMING_STOPS}, Maximum: {MAX_NUMBER_OF_UPCOMING_STOPS}.
                <br />
                Costs of generating ETAs are increased when greater than 9 stops. Please create a support ticket if
                you’re interested in generating to more than 9 stops.
              </>
            )}
          </div>
        ),
        selectedOption: _.toString(numberOfUpcomingStops),
        handleChangeOption: handleNumberOfUpcomingStopsChange,
        handleBlur: handleNumberOfUpcomingStopsBlur,
        isDisabled: () => !useETAService
      },
      {
        ...ContentNumericProp,
        contentTitle: "Assumed Dwell Time",
        contentInformation: (
          <div>
            Assumed ETA Dwell Time per stop.
            <br /> Maximum: {MAX_DWELL_MINUTES} minutes.
          </div>
        ),
        contentUnit: "minutes",
        selectedOption: _.toString(assumedDwellTime),
        handleChangeOption: handleAssumedDwellTimeChange,
        isDisabled: () => !useETAService
      }
    );

    if (etaWaitForEarlyTime) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Assume driver can't leave for next stop prior to the early time of the stop's time window",
        contentInformation: (
          <div>
            There is an acceptable time window generated for every stop in our system based on the data passed to us. An
            ‘early’ and a ‘late’ time for each stop.
            <br />
            <br />
            When calculating an ETA to the next stop, call it ‘Stop10’ it may be before the ‘early’ time of Stop10. When
            this setting is yes, we will assume the driver can’t progress to the following stop - Stop11 - until the
            early time of Stop10.
            <br />
            <br />
            How do we calculate the early and late time? The most straightforward is when an explicit early & late time
            window is defined in the data on the stop.
            <br />
            <br />
            If only a scheduled time is offered, it becomes more complex.
            <ul style={{paddingLeft: "20px"}}>
              <li>If it’s a pickup stop, the scheduled time is the early time. </li>
              <li>
                If it’s a ‘both’ or delivery stop, the scheduled time is the late time. The early time is ‘any time
                after the pickup stop(s) of this job are complete’.
              </li>
            </ul>
          </div>
        ),
        contentOptions: [
          {
            group: "waitForEarlyTimeWindow",
            label: "Yes",
            value: "true"
          },
          {
            group: "waitForEarlyTimeWindow",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleWaitForEarlyTimeWindowChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(waitForEarlyTimeWindow),
        isDisabled: () => !useETAService
      });
    }

    return retVal;
  }, [
    assumedDwellTime,
    gpsFrequency,
    handleAssumedDwellTimeChange,
    handleUseETAServiceChange,
    handleGPSFrequencyChange,
    handleNumberOfUpcomingStopsBlur,
    handleNumberOfUpcomingStopsChange,
    handleUseGPSPointsChange,
    handleWaitForEarlyTimeWindowChange,
    useETAService,
    isInternalSuperAdmin,
    numberOfUpcomingStops,
    useGPSPoints,
    etaWaitForEarlyTime,
    waitForEarlyTimeWindow
  ]);

  const handleUseETAService = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.useEtaService, true);
    setUseETAService(prefVal.value as boolean);
    setInitUseETAService(prefVal.value as boolean);
  }, []);

  const handleUseGPSPoints = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.useGPSPoints, true);
    setUseGPSPoints(prefVal.value as boolean);
    setInitUseGPSPoints(prefVal.value as boolean);
  }, []);

  const handleGPSFrequency = useCallback(
    (prefs: Pref[]) => {
      let prefVal = extractSimplePref(prefs, TenantPreferences.gpsFrequency, DEFAULT_GPS_FREQUENCY_MINUTES).value;
      //Old user setted value may not be in the options
      if (GPS_FREQUENCY_MINUTES_OPTIONS.find((option) => option.value === prefVal) === undefined) {
        prefVal = DEFAULT_GPS_FREQUENCY_MINUTES;
        updateTenantPrefs(TenantPreferences.gpsFrequency, prefVal);
      }
      setGPSFrequency(prefVal);
      setInitGPSFrequency(prefVal);
    },
    [updateTenantPrefs]
  );

  const handleNumberOfUpcomingStops = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<number> = extractSimplePref(
      prefs,
      TenantPreferences.numberOfUpcomingStops,
      DEFAULT_NUMBER_OF_UPCOMING_STOPS
    );
    setNumberOfUpcomingStops(prefVal.value as number);
    setInitNumberOfUpcomingStops(prefVal.value as number);
  }, []);

  const handleAssumedDwellTime = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<number> = extractSimplePref(
      prefs,
      TenantPreferences.assumedETADwellTime,
      DEFAULT_DWELL_MINUTES
    );
    setAssumedDwellTime(prefVal.value as number);
    setInitAssumedDwellTime(prefVal.value as number);
  }, []);

  const handleWaitForEarlyTimeWindow = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.waitForEarlyTimeWindow, false);
    setWaitForEarlyTimeWindow(Boolean(prefVal.value));
    setInitWaitForEarlyTimeWindow(Boolean(prefVal.value));
  }, []);

  useEffect(() => {
    log.debug("Tenant preferences loaded: ", tenantPreferences);
    handleUseETAService(tenantPreferences);
    handleUseGPSPoints(tenantPreferences);
    handleGPSFrequency(tenantPreferences);
    handleAssumedDwellTime(tenantPreferences);
    handleNumberOfUpcomingStops(tenantPreferences);
    handleWaitForEarlyTimeWindow(tenantPreferences);
  }, [
    handleAssumedDwellTime,
    handleNumberOfUpcomingStops,
    tenantPreferences,
    handleUseGPSPoints,
    handleGPSFrequency,
    handleUseETAService,
    handleWaitForEarlyTimeWindow
  ]);

  useEffect(() => {
    const data = setTenantPrefData?.setTenantPref as UserPref;
    log.debug("Pref saved: ", data);

    if (data && data.name === TenantPreferences.useEtaService) {
      handleUseETAService([data]);
    }

    if (data && data.name === TenantPreferences.useGPSPoints) {
      handleUseGPSPoints([data]);
    }
    if (data && data.name === TenantPreferences.gpsFrequency) {
      handleGPSFrequency([data]);
    }
    if (data && data.name === TenantPreferences.assumedETADwellTime) {
      handleAssumedDwellTime([data]);
    }
    if (data && data.name === TenantPreferences.numberOfUpcomingStops) {
      handleNumberOfUpcomingStops([data]);
    }
    if (data && data.name === TenantPreferences.waitForEarlyTimeWindow) {
      handleWaitForEarlyTimeWindow([data]);
    }
  }, [
    handleAssumedDwellTime,
    handleUseETAService,
    handleGPSFrequency,
    handleNumberOfUpcomingStops,
    handleUseGPSPoints,
    handleWaitForEarlyTimeWindow,
    setTenantPrefData?.setTenantPref
  ]);

  return (
    <Container>
      <Header>
        <HeaderText>ETA Settings</HeaderText>
      </Header>
      <Body>
        <Section sectionName="eta-section" sectionTitle="General Settings" render={renderETASettings} />
      </Body>
      <Footer>
        <CancelButton
          data-testid="cancel-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleCancelSetting}
          intent="none"
          outlined
        >
          Cancel
        </CancelButton>
        <SaveButton
          data-testid="save-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleSaveSetting}
          intent="primary"
        >
          Save
        </SaveButton>
      </Footer>
      <ConfirmationDialog
        testIdPrefix={"eta-service-confirmation"}
        isVisible={isOpenETAServiceConfirmationDialog}
        header={"Are you sure?"}
        body={
          "Are you sure you want to remove ETAs for all users in your company? This will remove ETAs on your dispatchers manifest cards, manifest details, and remove any color logic set up referencing ETAs. These can not be recovered and will need to be manually recreated."
        }
        yesText={"Yes"}
        noText={"Cancel"}
        handleYesClicked={handleYesETAServiceConfirm}
        handleNoClicked={handleCloseETAServiceConfirmationDialog}
        handleClose={handleCloseETAServiceConfirmationDialog}
      />
    </Container>
  );
};

export default ETA;
